import { useTranslation } from "react-i18next";
import { Shuffle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AnimalFigure } from "@/components/camp/AnimalFigure";
import { AnimalPicker } from "@/components/camp/AnimalPicker";
import {
  COLOURS,
  colourValue,
  type AnimalNames,
} from "@/components/camp/campData";
import {
  campIdentityFor,
  choiceForCamp,
  randomCampIdentity,
  type CampIdentity,
} from "@/components/room/campIdentity";
import { setAvatarChoice, useAvatarChoice } from "@/hooks/useAvatarChoice";
import {
  setDisplayName,
  setSuggestedDisplayName,
  useDisplayName,
} from "@/hooks/useDisplayName";
import { useHydrated } from "@/hooks/useHydrated";
import { suggestNickname } from "@/engine/state/nickname";
import { getSessionPeerId } from "@/engine/state/roomIdentity";
import { cn } from "@/engine/support/classNames";

function useAnimalNames(): AnimalNames {
  const { t } = useTranslation();
  return {
    fox: t("avatar.animals.fox"),
    owl: t("avatar.animals.owl"),
    bear: t("avatar.animals.bear"),
    hare: t("avatar.animals.hare"),
    wolf: t("avatar.animals.wolf"),
    turtle: t("avatar.animals.turtle"),
    deer: t("avatar.animals.deer"),
    cat: t("avatar.animals.cat"),
    frog: t("avatar.animals.frog"),
    hedge: t("avatar.animals.hedge"),
    mouse: t("avatar.animals.mouse"),
    raccoon: t("avatar.animals.raccoon"),
  };
}

/**
 * Who a room will see, and the whole of changing it: a brandbook animal, a
 * brandbook colour and a name, stored by the same hooks the room reads, so a
 * change here is what everybody at the fire sees. One editor, drawn on home
 * and on the profile page (#776), so the two can never disagree.
 */
export function CharacterEditor({
  className,
  aside,
}: {
  className?: string;
  /** What stands under the editor's fields — the profile's own settings. */
  aside?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const hydrated = useHydrated();
  const peerId = hydrated ? getSessionPeerId() : "";
  const identity = campIdentityFor(peerId, useAvatarChoice());
  const name = useDisplayName();
  const names = useAnimalNames();

  const choose = (next: CampIdentity) => setAvatarChoice(choiceForCamp(next));
  const shuffle = () => {
    const next = choiceForCamp(randomCampIdentity(identity));
    setSuggestedDisplayName(suggestNickname(peerId, next));
    setAvatarChoice(next);
  };

  return (
    <div
      data-testid="character-editor"
      className={cn("grid gap-6 md:grid-cols-[18rem_minmax(0,1fr)]", className)}
    >
      {/* Who a room will see, at the size it sees them. */}
      <section className="flex flex-col items-center gap-4 self-start rounded-3xl border border-border bg-background p-6">
        <AnimalFigure
          data-testid="profile-figure"
          data-animal={identity.animal}
          animal={identity.animal}
          colour={identity.colour}
          className="h-56 w-40"
        />
        <p className="max-w-full truncate text-lg font-semibold">
          {name || t("avatar.namePlaceholder")}
        </p>
        <Button variant="outline" onClick={shuffle}>
          <Shuffle aria-hidden="true" />
          {t("avatar.random")}
        </Button>
      </section>

      <div className="min-w-0 space-y-6">
        <label className="block space-y-2">
          <span className="text-sm font-medium">{t("avatar.yourName")}</span>
          <Input
            data-testid="character-name"
            value={name}
            placeholder={t("avatar.namePlaceholder")}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>

        <section className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
            {t("avatar.color")}
          </h2>
          <div
            role="radiogroup"
            aria-label={t("avatar.color")}
            className="grid grid-cols-5 gap-2.5 sm:grid-cols-10"
          >
            {COLOURS.map(([colour]) => (
              <button
                key={colour}
                type="button"
                role="radio"
                aria-checked={identity.colour === colour}
                aria-label={t(`avatar.colours.${colour}`)}
                onClick={() => choose({ ...identity, colour })}
                // The palette is the brandbook's tokens, not classes the
                // stylesheet would have to spell out for each colour.
                style={{ backgroundColor: colourValue(colour) }}
                className={cn(
                  "aspect-square w-full max-w-12 rounded-full transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring pointer-coarse:min-h-11",
                  identity.colour === colour &&
                    "ring-2 ring-primary ring-offset-2 ring-offset-background",
                )}
              />
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground">
            {t("avatar.animal")}
          </h2>
          <AnimalPicker
            value={identity.animal}
            colour={identity.colour}
            names={names}
            onSelect={(animal) => choose({ ...identity, animal })}
          />
        </section>

        {aside}
      </div>
    </div>
  );
}
